import { Injectable } from '@angular/core'
import {
    AcceptInviteRequest,
    AcceptInviteResponse,
    BankDetails,
    bankingDetailsPath,
    CreateMerchantRequest,
    CreateMerchantResponse,
    FirestoreStructure,
    InviteType,
    Merchant,
    MerchantMember,
    merchantMemberMapPath,
    merchantMembersPath,
    merchantsPath,
    MerchantTenant,
    merchantTenantsPath,
    Subscription,
    StorageStructure,
} from '@parkupp/core'
import { addDoc, collection, collectionData, deleteDoc, doc, docData, DocumentReference, Firestore, serverTimestamp, updateDoc, query, where, Query } from '@angular/fire/firestore'
import { Observable, take } from 'rxjs'
import { DocumentData } from 'rxfire/firestore/interfaces'
import * as firestore from 'firebase/firestore'
import { Auth } from '@angular/fire/auth'
import { HttpClient } from '@angular/common/http'
import { environment } from '../../environments/environment'
import { AuthenticationService } from './authentication.service'
import { map } from 'rxjs/operators'
import { Storage, ref, uploadBytes, getDownloadURL } from '@angular/fire/storage'
import { EnterpriseContextService } from './enterprise-context.service'

@Injectable({
    providedIn: 'root',
})
export class MerchantService {
    collection = collection(this.firestore, FirestoreStructure.MERCHANTS)
    sideMenuCollapsed: boolean = false

    constructor(
        public firestore: Firestore, 
        public auth: Auth, 
        private http: HttpClient, 
        private storage: Storage,
        private enterpriseContext: EnterpriseContextService
    ) {}

    getDocRef(merchantKey: string): any {
        return doc(this.firestore, `${FirestoreStructure.MERCHANTS}/${merchantKey}`)
    }

    get(merchantKey: string): Observable<any> {
        const clientDocRef = doc(this.firestore, `${FirestoreStructure.MERCHANTS}/${merchantKey}`)
        return docData(clientDocRef, { idField: '$key' })
    }

    getMerchant(merchantKey: string) {
        return new Promise<any>((resolve, reject) => {
            docData(doc(this.firestore, merchantsPath(), merchantKey)).subscribe((documentData: any) => {
                let merchant = new Merchant(documentData)
                merchant.$key = merchantKey
                resolve(merchant)
            })
        })
    }

    list(enterpriseKey?: string | null): Observable<Merchant[]> {
        const merchantsCollection = collection(this.firestore, merchantsPath())
        
        // Apply enterprise filter if specified or from context
        const filterKey = enterpriseKey ?? this.enterpriseContext.currentEnterpriseKey
        let merchantsQuery: Query<DocumentData> = merchantsCollection as Query<DocumentData>
        if (filterKey) {
            merchantsQuery = query(merchantsCollection, where('denormEnterpriseKey', '==', filterKey))
        }
        
        return collectionData(merchantsQuery, { idField: 'documentId' }).pipe(
            take(1),
            map((documents: DocumentData[]) =>
                documents
                    .map((merchantData) => {
                        const merchant = new Merchant(merchantData)
                        merchant.$key = merchantData['documentId']
                        return merchant
                    })
                    .filter((merchant) => merchant.companyName !== '' && !merchant.deletedAt)
            )
        )
    }
    
    /**
     * List all merchants without enterprise filter (for specific use cases)
     * Uses getDocs for a guaranteed one-time fetch (no partial data issues)
     */
    listAll(): Observable<Merchant[]> {
        const merchantsCollection = collection(this.firestore, merchantsPath())
        
        // Use getDocs for a one-time fetch instead of collectionData to avoid partial data issues
        return new Observable<Merchant[]>((subscriber) => {
            firestore.getDocs(merchantsCollection)
                .then((snapshot) => {
                    const merchants = snapshot.docs
                        .map((doc) => {
                            const merchant = new Merchant(doc.data())
                            merchant.$key = doc.id
                            return merchant
                        })
                        .filter((merchant) => merchant.companyName !== '' && !merchant.deletedAt)
                    
                    subscriber.next(merchants)
                    subscriber.complete()
                })
                .catch((error) => {
                    subscriber.error(error)
                })
        })
    }

    listPromise(): Promise<Merchant[]> {
        return new Promise<Merchant[]>((resolve, reject) => {
            this.list().subscribe((merchants) => {
                resolve(merchants)
            })
        })
    }

    createMerchant(createMerchantRequest: CreateMerchantRequest, idToken: string): Observable<CreateMerchantResponse> {
        return this.http.post<any>(`${environment.functionsUrl}/merchants-createMerchant`, createMerchantRequest, AuthenticationService.getHeaders(idToken))
    }

    updateMerchant(fields: { [id: string]: any }, uid: string): Promise<any> {
        fields['updatedAt'] = serverTimestamp() as firestore.Timestamp
        const reference = doc(this.firestore, merchantsPath(), uid)
        return updateDoc(reference, fields)
    }

    createOrUpdateMerchant(merchant: Merchant): Promise<any> {
        merchant.updatedAt = serverTimestamp() as firestore.Timestamp
        if (merchant.$key) {
            const reference = doc(this.firestore, merchantsPath(), merchant.$key)
            return updateDoc(reference, merchant.parse())
        } else {
            merchant.createdAt = serverTimestamp() as firestore.Timestamp
            return addDoc(collection(this.firestore, merchantsPath()), merchant.parse())
        }
    }

    subscribeToMerchant(uid: string): Observable<DocumentData> {
        return docData(doc(this.firestore, merchantsPath(), uid))
    }

    subscribeToMerchantMap(uid: string): Observable<DocumentData[]> {
        return collectionData(collection(this.firestore, merchantMemberMapPath(uid)), { idField: 'documentId' })
    }
    getAllBankDetails(uid: string): Observable<BankDetails[]> {
        return collectionData(collection(this.firestore, bankingDetailsPath(uid)), { idField: 'documentId' }).pipe(
            map((documents: DocumentData[]) =>
                documents.map((documentData) => {
                    const bankDetails = new BankDetails(documentData)
                    bankDetails.$key = documentData['documentId']
                    return bankDetails
                })
            )
        )
    }

    // saveMerchantBankDetails(bankDetails: BankDetails, merchantKey: string): Promise<any> {
    //     // bankDetails.updatedAt = serverTimestamp() as firestore.Timestamp
    //     if (bankDetails.$key) {
    //         const reference = doc(this.firestore, bankingDetailsPath(merchantKey), bankDetails.$key)
    //         return updateDoc(reference, bankDetails.parse())
    //     } else {
    //         // bankDetails.createdAt = serverTimestamp() as firestore.Timestamp
    //         return addDoc(collection(this.firestore, bankingDetailsPath(merchantKey)), bankDetails.parse())
    //     }
    // }

    addMerchantMember(merchantMember: MerchantMember, uid: string): Promise<any> {
        merchantMember.createdAt = serverTimestamp() as firestore.Timestamp
        merchantMember.updatedAt = serverTimestamp() as firestore.Timestamp
        return addDoc(collection(this.firestore, merchantMembersPath(uid)), merchantMember.parse())
    }

    updateMerchantMember(fields: { [id: string]: any }, uid: string, docId: string): Promise<any> {
        fields['updatedAt'] = serverTimestamp() as firestore.Timestamp
        return updateDoc(doc(this.firestore, merchantMembersPath(uid), docId), fields)
    }

    addMerchantTenant(merchantTenant: MerchantTenant, uid: string): Promise<any> {
        merchantTenant.createdAt = serverTimestamp() as firestore.Timestamp
        merchantTenant.updatedAt = serverTimestamp() as firestore.Timestamp
        return addDoc(collection(this.firestore, merchantTenantsPath(uid)), merchantTenant.parse())
    }

    updateMerchantTenant(fields: { [id: string]: any }, uid: string, docId: string): Promise<any> {
        fields['updatedAt'] = serverTimestamp() as firestore.Timestamp
        return updateDoc(doc(this.firestore, merchantTenantsPath(uid), docId), fields)
    }

    deleteMerchantMember(fields: { [id: string]: any }, uid: string, docId: string): Promise<any> {
        // Hard delete the merchant member document
        return deleteDoc(doc(this.firestore, merchantMembersPath(uid), docId))
    }

    softDeleteMerchant(merchantKey: string): Promise<any> {
        const fields = {
            deletedAt: serverTimestamp() as firestore.Timestamp,
            updatedAt: serverTimestamp() as firestore.Timestamp,
        }
        return updateDoc(doc(this.firestore, merchantsPath(), merchantKey), fields)
    }

    acceptMerchantInvite(merchantKey: string, uid: string, email: string, token: string, idToken: string, inviteType: InviteType): Observable<AcceptInviteResponse> {
        const acceptMerchantInviteRequest = new AcceptInviteRequest()
        acceptMerchantInviteRequest.inviteType = inviteType
        acceptMerchantInviteRequest.merchantKey = merchantKey
        acceptMerchantInviteRequest.uid = uid
        acceptMerchantInviteRequest.token = token
        acceptMerchantInviteRequest.email = email
        return this.http.post<AcceptInviteResponse>(`${environment.functionsUrl}/merchants-acceptMerchantIMemberInvite`, acceptMerchantInviteRequest, AuthenticationService.getHeaders(idToken))
    }

    populateSubscriptionsArray(ref: any) {
        return new Promise<any>((resolve, reject) => {
            collectionData(ref, { idField: 'documentId' }).subscribe((documents: any[]) => {
                let subscriptions: Subscription[] = []
                documents.forEach((documentData) => {
                    let subscription = new Subscription(documentData)
                    subscription.$key = documentData.documentId

                    // docData(documentData.clientRef).subscribe((data) => {
                    //     subscription.$client = new Client(data)
                    //     subscription.$client.$key = documentData.clientRef.id
                    // })
                    //
                    // docData(documentData.parkingRef).subscribe((data) => {
                    //     subscription.$parking = new Parking(data)
                    //     subscription.$parking.$key = documentData.parkingRef.id
                    // })

                    // subscription.loadClient(firestore).then(() => {
                    //     console.log(subscription)
                    // })

                    subscriptions.push(subscription)
                })
                resolve(subscriptions)
            })
        })
    }

    toggleSideMenu() {
        this.sideMenuCollapsed = !this.sideMenuCollapsed
    }

    // Upload white-label logo image directly (following parking layout pattern)
    uploadWhitelabelLogo(merchantKey: string, logoFile: File): Promise<string> {
        const fileExtension = logoFile.name.split('.').pop()
        const fileName = `logo.${fileExtension}`
        const filePath = `${StorageStructure.WHITELABEL}/${merchantKey}/${fileName}`

        return uploadBytes(ref(this.storage, filePath), logoFile)
            .then(() => {
                return getDownloadURL(ref(this.storage, filePath))
            })
            .catch((error) => {
                console.error('Upload failed:', error)
                throw error
            })
    }
}
